import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Table, Spin, Tooltip, Tag, TreeSelect, Space, Empty, Button } from 'antd';
import { Checkbox } from 'antd';
import {
	PaperClipOutlined,
	ArrowUpOutlined,
	ArrowDownOutlined,
	HistoryOutlined,
	CloseOutlined,
	QuestionCircleOutlined,
	
} from '@ant-design/icons';
import { NumericFormat } from 'react-number-format';
import dayjs from 'dayjs';
import styles from './index.module.scss';
import { ExpenseTransaction } from '../ExpenseTransactionMain';
import HistoryModal from '../HistoryModal';
import {
	toastText,
	getEntityType,
	generateQBOUrl,
	getOptionLabel,
} from 'utils/utils';
import { QuickbooksEmployee, TickSmall } from 'utils/svgs';
import { apiConfig } from 'redux/apis';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { updateTransactionLineItemAction } from 'redux/action/transactionAction';

const endPoint = process.env.REACT_APP_API_ENDPOINT;

// Look up isProject flag from tree-structured customer options
const getOptionIsProject = (value: string, options: any[]): boolean => {
	for (const node of options) {
		if (node.value === value) return node.isProject === true;
		if (node.children && node.children.length > 0) {
			const found = getOptionIsProject(value, node.children);
			if (found) return true;
		}
	}
	return false;
};

/** Column keys that can be toggled in "Visible Columns" (select & actions are always shown) */
export const VISIBLE_COLUMN_OPTIONS: { key: string; label: string }[] = [
	{ key: 'txnId', label: 'ID' },
	{ key: 'entityType', label: 'Type' },
	{ key: 'lineBasedOn', label: 'Line Basis' },
	{ key: 'txnDate', label: 'Date' },
	{ key: 'vendorName', label: 'Vendor' },
	{ key: 'accountName', label: 'Account' },
	{ key: 'customerName', label: 'Customer' },
	{ key: 'className', label: 'Class' },
	{ key: 'locationName', label: 'Location' },
	{ key: 'description', label: 'Memo' },
	{ key: 'amount', label: 'Amount' },
	{ key: 'attachable', label: 'Attachments' },
];

export const DEFAULT_VISIBLE_COLUMN_KEYS = VISIBLE_COLUMN_OPTIONS.map((o) => o.key);

interface TableProps {
	data: ExpenseTransaction[];
	loading: boolean;
	onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
	currentSort?: { sortBy: string; sortOrder: 'asc' | 'desc' };
	hasMore?: boolean;
	isLoadingMore?: boolean;
	onLoadMore?: () => void;
	selectedRowKeys?: string[];
	isHeaderChecked?: boolean;
	onHeaderCheckboxChange?: (checked: boolean) => void;
	onRowCheckboxChange?: (rowKey: string, checked: boolean) => void;
	grandTotal?: number;
	accountOptions?: any[];
	customerOptions?: any[];
	classOptions?: any[];
	locationOptions?: any[];
	dateRange?: [dayjs.Dayjs, dayjs.Dayjs];
	selectedAccounts?: string[];
	selectedCustomers?: string[];
	selectedClasses?: string[];
	selectedLocations?: string[];
	selectedEntityTypes?: string[];
	selectedLineBasedOn?: string[];
	onEntityTypeFilterChange?: (values: string[]) => void;
	onLineBasedOnFilterChange?: (values: string[]) => void;
	/** Keys of columns to show; if omitted, all toggleable columns are shown */
	visibleColumnKeys?: string[];
}

const ExpenseTransactionTable: React.FC<TableProps> = ({
	data,
	loading,
	onSort,
	currentSort,
	hasMore,
	isLoadingMore,
	onLoadMore,
	selectedRowKeys = [],
	isHeaderChecked = false,
	onHeaderCheckboxChange,
	onRowCheckboxChange,
	grandTotal = 0,
	accountOptions = [],
	customerOptions = [],
	classOptions = [],
	locationOptions = [],
	selectedAccounts = [],
	selectedCustomers = [],
	selectedClasses = [],
	selectedLocations = [],
	selectedEntityTypes = [],
	selectedLineBasedOn = [],
	onEntityTypeFilterChange,
	onLineBasedOnFilterChange,
	visibleColumnKeys,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
	const [loadingRowKeys, setLoadingRowKeys] = useState<Set<string>>(new Set());
	const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
	const [showNoMoreMessage, setShowNoMoreMessage] = useState(false);
	const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
	const [editingValues, setEditingValues] = useState<{
		[key: string]: Partial<ExpenseTransaction>;
	}>({});
	const [savingRowKey, setSavingRowKey] = useState<string | null>(null);
	const [entityTypeFilterDraft, setEntityTypeFilterDraft] = useState<string[]>(selectedEntityTypes);
	const [lineBasedOnFilterDraft, setLineBasedOnFilterDraft] = useState<string[]>(selectedLineBasedOn);
	const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({
		select: 50,
		txnId: 100,
		entityType: 150,
		lineBasedOn: 150,
		txnDate: 120,
		vendorName: 150,
		accountName: 150,
		customerName: 150,
		className: 150,
		locationName: 150,
		memo: 200,
		amount: 120,
		attachable: 130,
		actions: 120,
	});
	const resizeRef = useRef<{
		columnKey: string;
		startX: number;
		startWidth: number;
	} | null>(null);

	const { configurations: configurationsData } = useSelector(
		(state: any) => state.companies
	);

	useEffect(() => {
		setEntityTypeFilterDraft(selectedEntityTypes);
	}, [selectedEntityTypes]);

	useEffect(() => {
		setLineBasedOnFilterDraft(selectedLineBasedOn);
	}, [selectedLineBasedOn]);

	// Server-side sorting
	const handleSort = (key: string) => {
		if (onSort) {
			const newSortOrder = currentSort?.sortBy === key && currentSort?.sortOrder === 'asc' ? 'desc' : 'asc';
			onSort(key, newSortOrder);
		}
	};

	// Reset scroll tracking when hasMore changes
	useEffect(() => {
		if (hasMore) {
			setHasScrolledToBottom(false);
			setShowNoMoreMessage(false);
		}
	}, [hasMore]);

	// Auto-hide "No more transactions" message after 3 seconds
	useEffect(() => {
		if (!hasMore && !isLoadingMore && hasScrolledToBottom) {
			setShowNoMoreMessage(true);
			const timer = setTimeout(() => {
				setShowNoMoreMessage(false);
			}, 3000);

			return () => {
				clearTimeout(timer);
			};
		} else {
			setShowNoMoreMessage(false);
		}
	}, [hasMore, isLoadingMore, hasScrolledToBottom]);

	// Infinite scroll logic
	useEffect(() => {
		const handleScroll = (event: any) => {
			const { scrollTop, scrollHeight, clientHeight } = event.target;
			const threshold = 10; // Load more when 10px from bottom (same as TimeActivity)

			if (scrollTop + clientHeight >= scrollHeight - threshold) {
				if (!hasMore) {
					// User scrolled to bottom but no more data
					setHasScrolledToBottom(true);
					return;
				}
				if (!isLoadingMore && !loading) {
					onLoadMore?.();
				}
			}
		};

		// Attach scroll listener to the actual table body element
		const tableBody = document.querySelector('.ant-table-body');
		if (tableBody) {
			tableBody.addEventListener('scroll', handleScroll);
			return () => {
				tableBody.removeEventListener('scroll', handleScroll);
			};
		}
	}, [hasMore, isLoadingMore, loading, onLoadMore]);
	const handleClick = async (expenseTransaction: ExpenseTransaction) => {
		const rowKey = `${expenseTransaction.txnId}:${expenseTransaction.qboLineId || 'no-line'
			}`;
		if (loadingRowKeys.has(rowKey)) return;

		try {
			setLoadingRowKeys((prev) => new Set(prev).add(rowKey));
			await exportAttachment(expenseTransaction.txnId, expenseTransaction);
		} finally {
			setLoadingRowKeys((prev) => {
				const next = new Set(prev);
				next.delete(rowKey);
				return next;
			});
		}
	};
	const exportAttachment = async (
		txnId: string,
		transaction?: ExpenseTransaction
	) => {
		console.log('exportAttachment called for txnId:', txnId);
		const companyId = localStorage.getItem('companyId');
		if (!txnId) return;

		try {
			const config = await apiConfig();

			// Format tokens correctly
			const accessToken = config.headers?.Authorization;
			const refreshToken = config.headers?.RefreshToken;

			console.log('transaction', transaction);
			// Determine entityType dynamically from transaction data
			const entityType = transaction ? getEntityType(transaction) : 'Purchase';

			// 🚀 Send body + headers
			const response = await axios.post(
				`${endPoint}/attachments/download`,
				{
					companyId,
					transactionIds: [txnId],
					entityType,
				},
				{
					responseType: 'blob',
					headers: {
						Authorization: accessToken,
						RefreshToken: refreshToken,
						'Content-Type': 'application/json',
					},
				}
			);

			// Extract filename
			let fileName = 'download.zip';
			const disposition = response.headers['content-disposition'];
			if (disposition) {
				let match = disposition.match(/filename\*=(?:UTF-8''|)([^;]+)/);
				if (match && match[1]) {
					fileName = decodeURIComponent(match[1]);
				} else {
					match = disposition.match(/filename="?([^"]+)"?/);
					if (match && match[1]) {
						fileName = match[1];
					}
				}
			}

			// Trigger download
			const blob = new Blob([response.data], {
				type: response.headers['content-type'] || 'application/zip',
			});
			const url = window.URL.createObjectURL(blob);

			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', fileName);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
			toastText(
				'Attachments for the transaction downloaded successfully',
				'success'
			);
			// setLoadingTxnId(null);
		} catch (error: any) {
			console.error(error);
			if (error?.response?.status === 400) {
				toastText(
					error?.response?.data?.message ||
					'No attachments found for given transactions',
					'error'
				);
			} else {
				toastText('Error while exporting attachments', 'error');
			}
		}
	};

	const getSortIcon = (key: string) => {
		// Determine what the next sort order will be
		const getNextSortOrder = () => {
			if (currentSort?.sortBy !== key) {
				return 'ascending';
			}
			return currentSort.sortOrder === 'asc' ? 'descending' : 'ascending';
		};

		const nextSortOrder = getNextSortOrder();
		const tooltipTitle = `Click to sort ${nextSortOrder}`;

		if (currentSort?.sortBy !== key) {
			return (
				<Tooltip title={tooltipTitle}>
					<ArrowUpOutlined className={styles['sort-icon-inactive']} />
				</Tooltip>
			);
		}
		return currentSort.sortOrder === 'asc' ? (
			<Tooltip title={tooltipTitle}>
				<ArrowUpOutlined className={styles['sort-icon-active']} />
			</Tooltip>
		) : (
			<Tooltip title={tooltipTitle}>
				<ArrowDownOutlined className={styles['sort-icon-active']} />
			</Tooltip>
		);
	};

	const getEntityTypeColor = (entityType: string) => {
		const colorMap: { [key: string]: string } = {
			deposit: 'cyan',
			purchase: 'geekblue',
			journalentry: 'green',
			bill: 'volcano',
			vendorcredit: 'orange',
			invoice: 'purple',
			creditmemo: 'magenta',
			refundreceipt: 'red',
			salesreceipt: 'gold',
		};
		return colorMap[entityType.toLowerCase()] || '#666666';
	};

	const getLineBasedOnColor = (lineBasedOn: string) => {
		const colorMap: { [key: string]: string } = {
			account: 'green',
			item: 'blue',
		};
		return colorMap[lineBasedOn.toLowerCase()] || '#666666';
	};

	const ENTITY_TYPE_OPTIONS = [
		'Bill',
		'CreditMemo',
		'Deposit',
		'Invoice',
		'JournalEntry',
		'Purchase',
		'RefundReceipt',
		'SalesReceipt',
		'VendorCredit',
	];

	const LINE_BASED_ON_OPTIONS = ['Account', 'Item'];

	const renderFilterDropdown = ({
		title,
		options,
		value,
		onChange,
		onApply,
	}: {
		title: string;
		options: string[];
		value: string[];
		onChange: (next: string[]) => void;
		onApply: () => void;
	}) => (
		<div className={styles['table-filter-dropdown']}>
			<div className={styles['table-filter-options']}>
				<div className={styles['table-filter-title']}>{title}</div>
				<Checkbox.Group
					value={value}
					onChange={(checkedValues) => onChange(checkedValues as string[])}
				>
					<Space direction="vertical" size={6}>
						{options.map((option) => (
							<Checkbox key={option} value={option}>
								{option}
							</Checkbox>
						))}
					</Space>
				</Checkbox.Group>
			</div>
			<div className={styles['table-filter-footer']}>
				<Button
					type="primary"
					className={styles['table-filter-apply']}
					onClick={onApply}
				>
					Apply
				</Button>
			</div>
		</div>
	);

	// Helper function to normalize display values - treat "Blank Customer/Class/Location" as empty
	const normalizeDisplayValue = (value: string | undefined | null): string => {
		if (!value) return '';
		const normalized = value.trim();
		if (normalized === 'Blank Customer' || normalized === 'Blank Class' || normalized === 'Blank Location') {
			return '';
		}
		return normalized;
	};

	const handleHistoryClick = () => {
		setIsHistoryModalVisible(true);
	};

	const handleHistoryModalClose = () => {
		setIsHistoryModalVisible(false);
	};

	// Handle starting edit mode for a row
	const handleStartEdit = (record: ExpenseTransaction) => {
		const rowKey = `${record.txnId}:${record.qboLineId || 'no-line'}:${record.entityType
			}`;
		setEditingRowKey(rowKey);
		setEditingValues({
			[rowKey]: {
				accountId: record.accountId,
				accountName: record.accountName,
				customerId: record.customerId,
				customerName: record.customerName,
				classId: record.classId,
				className: record.className,
				locationId: record.locationId,
				locationName: record.locationName,
			},
		});
	};

	// Handle canceling edit mode
	const handleCancelEdit = () => {
		setEditingRowKey(null);
		setEditingValues({});
	};

	// Handle saving changes
	const handleSaveEdit = async (record: ExpenseTransaction) => {
		const rowKey = `${record.txnId}:${record.qboLineId || 'no-line'}:${record.entityType
			}`;
		const editedValues = editingValues[rowKey];

		if (!editedValues) {
			handleCancelEdit();
			return;
		}

		setSavingRowKey(rowKey);
		try {
			const updateParams: {
				txnId: string;
				lineId: string;
				entityType: string;
				accountRef?: { value: string; name: string };
				customerRef?: { value: string; name: string; isProject?: boolean };
				classRef?: { value: string; name: string };
				locationRef?: { value: string; name: string };
			} = {
				txnId: record.txnId,
				lineId: record.qboLineId || 'no-line',
				entityType: record.entityType,
			};

			// Add account reference if changed
			// Disable account update when linebasedon is 'Item'
			const isAccountDisabled = (record as any).lineBasedOn === 'Item';
			if (
				!isAccountDisabled &&
				editedValues.accountId !== record.accountId &&
				editedValues.accountId
			) {
				updateParams.accountRef = {
					value: editedValues.accountId,
					name:
						editedValues.accountName ||
						getOptionLabel(editedValues.accountId, accountOptions),
				};
			}

			// Add customer reference if changed
			if (editedValues.customerId !== record.customerId) {
				updateParams.customerRef = {
					value: editedValues.customerId || '',
					name:
						editedValues.customerId === '' || editedValues.customerId === undefined
							? ''
							: editedValues.customerName ||
							(editedValues.customerId
								? getOptionLabel(editedValues.customerId, customerOptions)
								: ''),
					isProject: editedValues.customerId
						? getOptionIsProject(editedValues.customerId, customerOptions)
						: false,
				};
			}

			// Add class reference if changed
			if (editedValues.classId !== record.classId) {
				updateParams.classRef = {
					value: editedValues.classId || '',
					name:
						editedValues.classId === '' || editedValues.classId === undefined
							? ''
							: editedValues.className ||
							(editedValues.classId
								? getOptionLabel(editedValues.classId, classOptions)
								: ''),
				};
			}

			// Add location reference if changed
			if (editedValues.locationId !== record.locationId) {
				updateParams.locationRef = {
					value: editedValues.locationId || '',
					name:
						editedValues.locationId === '' || editedValues.locationId === undefined
							? ''
							: editedValues.locationName ||
							(editedValues.locationId
								? getOptionLabel(editedValues.locationId, locationOptions)
								: ''),
				};
			}

			// Only proceed if there are actual changes
			if (
				!updateParams.accountRef &&
				!updateParams.customerRef &&
				!updateParams.classRef &&
				!updateParams.locationRef
			) {
				handleCancelEdit();
				return;
			}

			const result = await dispatch(
				updateTransactionLineItemAction({
					...updateParams,
					accountIds: selectedAccounts,
					customerIds: selectedCustomers,
					classIds: selectedClasses,
					locationIds: selectedLocations,
				})
			);

			if (updateTransactionLineItemAction.fulfilled.match(result)) {
				toastText(`Transaction #${record.txnId} updated successfully in QuickBooks`, 'success');
				handleCancelEdit();
				// Transaction is updated in Redux state directly, no need to refetch
			} else {
				const message =
					(typeof result.payload === 'object' && result.payload && 'message' in result.payload)
						? (result.payload as { message?: string }).message
						: undefined;
				toastText(message || 'Failed to update transaction', 'error');
			}
		} catch (error: any) {
			console.error('Save edit error:', error);
			toastText(
				error?.response?.data?.message || 'Error while updating transaction',
				'error'
			);
		} finally {
			setSavingRowKey(null);
		}
	};

	// Handle field change in edit mode
	const handleFieldChange = (
		rowKey: string,
		field: string,
		value: string,
		label?: string
	) => {
		// If value is empty (blank selection), store empty string for name
		// Otherwise, use the label or value
		const nameValue = value === '' ? '' : (label || value);
		setEditingValues((prev) => ({
			...prev,
			[rowKey]: {
				...prev[rowKey],
				[field]: value,
				[field === 'accountId'
					? 'accountName'
					: field === 'customerId'
						? 'customerName'
						: field === 'classId'
							? 'className'
							: 'locationName']: nameValue,
			},
		}));
	};

	// Column resizing handlers
	const handleMouseDown = useCallback((e: React.MouseEvent, columnKey: string) => {
		e.preventDefault();
		e.stopPropagation();
		const startX = e.clientX;
		const startWidth = columnWidths[columnKey] || 100;

		resizeRef.current = { columnKey, startX, startWidth };

		const handleMouseMove = (e: MouseEvent) => {
			if (!resizeRef.current) return;

			const currentRef = resizeRef.current;
			const diff = e.clientX - currentRef.startX;
			const minWidth = currentRef.columnKey === 'vendorName' ? 150 : 110;
			const newWidth = Math.max(minWidth, currentRef.startWidth + diff);

			setColumnWidths((prev) => ({
				...prev,
				[currentRef.columnKey]: newWidth,
			}));
		};

		const handleMouseUp = () => {
			resizeRef.current = null;
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
		document.body.style.cursor = 'col-resize';
		document.body.style.userSelect = 'none';
	}, [columnWidths]);

	const columns: any[] = [
		{
			title: (
				<Checkbox
					checked={isHeaderChecked}
					onChange={(e) => onHeaderCheckboxChange?.(e.target.checked)}
				/>
			),
			dataIndex: 'select',
			key: 'select',
			fixed: 'left',
			width: columnWidths.select,
			render: (_: any, record: ExpenseTransaction) => {
				const rowKey = `${record.txnId}:${record.qboLineId || 'no-line'}:${record.entityType
					}`;
				return (
					<Checkbox
						checked={selectedRowKeys.includes(rowKey)}
						onChange={(e) => onRowCheckboxChange?.(rowKey, e.target.checked)}
					/>
				);
			},
		},
		{
			title: (
				<div style={{ position: 'relative', width: '100%' }}>
					ID
					<div
						className={styles['resize-handle']}
						onMouseDown={(e) => handleMouseDown(e, 'txnId')}
					/>
				</div>
			),
			dataIndex: 'txnId',
			key: 'txnId',
			ellipsis: true,
			fixed: 'left',
			width: columnWidths.txnId,
			render: (txnId: string, record: ExpenseTransaction) => {
				const qboUrl = generateQBOUrl(record.entityType, txnId, record?.paymentType);

				return (
					<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
						<Tooltip title={`Open in QuickBooks`}>
							<a
								href={qboUrl}
								target="_blank"
								rel="noopener noreferrer"
								style={{ display: 'flex', alignItems: 'center', color: '#2C9F1C', gap: '4px' }}
							>
								<span>#{txnId} </span>
								<QuickbooksEmployee />
							</a>
						</Tooltip>
					</div>
				);
			},
		},
		{
			title: (
				<div className={styles['filterable-header']}>
					<div
						className={styles['sortable-header']}
						onClick={() => handleSort('entityType')}
					>
						Type
						{getSortIcon('entityType')}
					</div>
				</div>
			),
			dataIndex: 'entityType',
			key: 'entityType',
			ellipsis: true,
			width: columnWidths.entityType,
			filters: ENTITY_TYPE_OPTIONS.map((option) => ({
				text: option,
				value: option,
			})),
			filterMultiple: true,
			filteredValue: selectedEntityTypes.length > 0 ? selectedEntityTypes : null,
			onFilterDropdownOpenChange: (open: boolean) => {
				if (open) {
					setEntityTypeFilterDraft(selectedEntityTypes);
				}
			},
			filterDropdown: ({ close }: { close: () => void }) =>
				renderFilterDropdown({
					title: 'Entity Type',
					options: ENTITY_TYPE_OPTIONS,
					value: entityTypeFilterDraft,
					onChange: setEntityTypeFilterDraft,
					onApply: () => {
						onEntityTypeFilterChange?.(entityTypeFilterDraft);
						close();
					},
				}),
			render: (entityType: string) => (

				<Tag bordered={false} color={getEntityTypeColor(entityType)}>{entityType}</Tag>
			),
		},
		{
			title: (
				<div className={styles['filterable-header']}>
					<div
						className={styles['sortable-header']}
						onClick={() => handleSort('lineBasedOn')}
					>
						Line Basis
						{getSortIcon('lineBasedOn')}
					</div>
				</div>
			),
			dataIndex: 'lineBasedOn',
			key: 'lineBasedOn',
			ellipsis: true,
			width: columnWidths.lineBasedOn,
			filters: LINE_BASED_ON_OPTIONS.map((option) => ({
				text: option,
				value: option,
			})),
			filterMultiple: true,
			filteredValue: selectedLineBasedOn.length > 0 ? selectedLineBasedOn : null,
			onFilterDropdownOpenChange: (open: boolean) => {
				if (open) {
					setLineBasedOnFilterDraft(selectedLineBasedOn);
				}
			},
			filterDropdown: ({ close }: { close: () => void }) =>
				renderFilterDropdown({
					title: 'Line Basis',
					options: LINE_BASED_ON_OPTIONS,
					value: lineBasedOnFilterDraft,
					onChange: setLineBasedOnFilterDraft,
					onApply: () => {
						onLineBasedOnFilterChange?.(lineBasedOnFilterDraft);
						close();
					},
				}),
			render: (lineBasedOn: string) => (
				<Tag bordered={false} color={getLineBasedOnColor(lineBasedOn)}>
					{lineBasedOn === 'Item' ? 'Item' : 'Account'}
				</Tag>
			),
		},
		{
			title: (
				<div style={{ position: 'relative', width: '100%' }}>
					<div
						className={styles['sortable-header']}
						onClick={() => handleSort('txnDate')}
					>
						Date
						{getSortIcon('txnDate')}
					</div>
					<div
						className={styles['resize-handle']}
						onMouseDown={(e) => handleMouseDown(e, 'txnDate')}
					/>
				</div>
			),
			dataIndex: 'txnDate',
			key: 'txnDate',
			ellipsis: true,
			width: columnWidths.txnDate,
			render: (date: string) => {
				if (!date) return '';
				return dayjs(date).format('MM/DD/YYYY');
			},
		},
		{
			title: (
				<div style={{ position: 'relative', width: '100%' }}>
					<div
						className={styles['sortable-header']}
						onClick={() => handleSort('vendorName')}
					>
						Vendor
						{getSortIcon('vendorName')}
					</div>
					<div
						className={styles['resize-handle']}
						onMouseDown={(e) => handleMouseDown(e, 'vendorName')}
					/>
				</div>
			),
			dataIndex: 'vendorName',
			key: 'vendorName',
			ellipsis: true,
			width: columnWidths.vendorName,
			render: (vendorName: string) => vendorName ?? '',
		},
		{
			// title: 'Account',
			title: (
				<div style={{ position: 'relative', width: '100%' }}>
					<div
						className={styles['sortable-header']}
						onClick={() => handleSort('accountName')}
					>
						Account
						{getSortIcon('accountName')}
					</div>
					<div
						className={styles['resize-handle']}
						onMouseDown={(e) => handleMouseDown(e, 'accountName')}
					/>
				</div>
			),
			dataIndex: 'accountName',
			key: 'accountName',
			ellipsis: true,
			width: columnWidths.accountName,
			render: (accountName: string, record: ExpenseTransaction) => {
				const rowKey = `${record.txnId}:${record.qboLineId || 'no-line'}:${record.entityType
					}`;
				const isEditing = editingRowKey === rowKey;
				const editedValues = editingValues[rowKey];
				const currentAccountId =
					isEditing && editedValues ? editedValues.accountId : record.accountId;
				// Disable account update when linebasedon is 'Item'
				const isAccountDisabled = (record as any).lineBasedOn === 'Item';

				if (isEditing) {
					return (
						<div className={styles['expense-allocation-table-tree-select-wrapper']}>
							<TreeSelect
								showSearch
								style={{ width: '100%' }}
								value={currentAccountId}
								disabled={isAccountDisabled}
								dropdownStyle={{
									maxHeight: 400,
									overflow: 'auto',
									minWidth: 250,
								}}
								treeData={accountOptions}
								placeholder="Account"
								allowClear={false}
								treeDefaultExpandAll
								treeLine
								size="large"
								onChange={(value, label) => {
									const labelStr =
										Array.isArray(label) && label[0]
											? String(label[0])
											: label
												? String(label)
												: '';
									handleFieldChange(rowKey, 'accountId', value, labelStr);
								}}
								filterTreeNode={(inputValue, treeNode) => {
									return (
										treeNode.title
											?.toString()
											.toLowerCase()
											.includes(inputValue.toLowerCase()) || false
									);
								}}
							/>
							{isAccountDisabled && (
								<Tooltip title="This is an item-based transaction. Account cannot be updated here.">
									<div className={styles['question-badge-container']}>
										<div className={styles['question-badge']}>
											<QuestionCircleOutlined className={styles['question-icon']} />
										</div>
									</div>
								</Tooltip>
							)}
						</div>
					);
				}
				return (
					<span
						onClick={() => handleStartEdit(record)}
						style={{ cursor: 'pointer' }}
					>
						{accountName}
					</span>
				);
			},
		},
		{
			// title: 'Customer',
			title: (
				<div style={{ position: 'relative', width: '100%' }}>
					<div
						className={styles['sortable-header']}
						onClick={() => handleSort('customerName')}
					>
						Customer
						{getSortIcon('customerName')}
					</div>
					<div
						className={styles['resize-handle']}
						onMouseDown={(e) => handleMouseDown(e, 'customerName')}
					/>
				</div>
			),
			dataIndex: 'customerName',
			key: 'customerName',
			ellipsis: true,
			width: columnWidths.customerName,
			render: (customerName: string, record: ExpenseTransaction) => {
				const rowKey = `${record.txnId}:${record.qboLineId || 'no-line'}:${record.entityType
					}`;
				const isEditing = editingRowKey === rowKey;
				const editedValues = editingValues[rowKey];
				const currentCustomerId =
					isEditing && editedValues
						? editedValues.customerId
						: record.customerId;

				if (isEditing) {
					const customerTreeData = [
						{
							title: 'Blank Customer',
							value: '',
							key: 'blank',
						},
						...customerOptions,
					];
					return (
						<TreeSelect
							showSearch
							style={{ width: '100%' }}
							value={currentCustomerId || ''}
							dropdownStyle={{
								maxHeight: 400,
								minWidth: 250,
								overflow: 'auto',
							}}
							treeData={customerTreeData}
							placeholder="Customer"
							allowClear={false}
							treeDefaultExpandAll
							treeLine
							size="large"
							onChange={(value, label) => {
								const labelStr =
									Array.isArray(label) && label[0]
										? String(label[0])
										: label
											? String(label)
											: '';
								handleFieldChange(rowKey, 'customerId', value || '', labelStr);
							}}
							filterTreeNode={(inputValue, treeNode) => {
								return (
									treeNode.title
										?.toString()
										.toLowerCase()
										.includes(inputValue.toLowerCase()) || false
								);
							}}
						/>
					);
				}
				return (
					<div
						onClick={() => handleStartEdit(record)}
						style={{
							cursor: 'pointer',
							padding: '4px 0',
							minHeight: '22px',
							width: '100%',
						}}
					>
						{normalizeDisplayValue(customerName) || <span style={{ opacity: 0.5 }}></span>}
					</div>
				);
			},
		},
		{
			// title: 'Class',
			title: (
				<div style={{ position: 'relative', width: '100%' }}>
					<div
						className={styles['sortable-header']}
						onClick={() => handleSort('className')}
					>
						Class
						{getSortIcon('className')}
					</div>
					<div
						className={styles['resize-handle']}
						onMouseDown={(e) => handleMouseDown(e, 'className')}
					/>
				</div>
			),
			dataIndex: 'className',
			key: 'className',
			ellipsis: true,
			width: columnWidths.className,
			render: (className: string, record: ExpenseTransaction) => {
				const rowKey = `${record.txnId}:${record.qboLineId || 'no-line'}:${record.entityType
					}`;
				const isEditing = editingRowKey === rowKey;
				const editedValues = editingValues[rowKey];
				const currentClassId =
					isEditing && editedValues ? editedValues.classId : record.classId;

				if (isEditing) {
					const classTreeData = [
						{
							title: 'Blank Class',
							value: '',
							key: 'blank',
						},
						...classOptions,
					];
					return (
						<TreeSelect
							showSearch
							style={{ width: '100%' }}
							value={currentClassId || ''}
							dropdownStyle={{
								maxHeight: 400,
								overflow: 'auto',
								minWidth: 250,
							}}
							treeData={classTreeData}
							placeholder="Class"
							allowClear={false}
							treeDefaultExpandAll
							treeLine
							size="large"
							onChange={(value, label) => {
								const labelStr =
									Array.isArray(label) && label[0]
										? String(label[0])
										: label
											? String(label)
											: '';
								handleFieldChange(rowKey, 'classId', value || '', labelStr);
							}}
							filterTreeNode={(inputValue, treeNode) => {
								return (
									treeNode.title
										?.toString()
										.toLowerCase()
										.includes(inputValue.toLowerCase()) || false
								);
							}}
						/>
					);
				}
				return (
					<div
						onClick={() => handleStartEdit(record)}
						style={{
							cursor: 'pointer',
							padding: '4px 0',
							minHeight: '22px',
							width: '100%',
						}}
					>
						{normalizeDisplayValue(className) || <span style={{ opacity: 0.5 }}></span>}
					</div>
				);
			},
		},
		{
			title: (
				<div style={{ position: 'relative', width: '100%' }}>
					<div
						className={styles['sortable-header']}
						onClick={() => handleSort('locationName')}
					>
						Location
						{getSortIcon('locationName')}
					</div>
					<div
						className={styles['resize-handle']}
						onMouseDown={(e) => handleMouseDown(e, 'locationName')}
					/>
				</div>
			),
			// title: 'Location',
			dataIndex: 'locationName',
			key: 'locationName',
			ellipsis: true,
			width: columnWidths.locationName,
			render: (locationName: string, record: ExpenseTransaction) => {
				const rowKey = `${record.txnId}:${record.qboLineId || 'no-line'}:${record.entityType
					}`;
				const isEditing = editingRowKey === rowKey;
				const editedValues = editingValues[rowKey];
				const currentLocationId =
					isEditing && editedValues
						? editedValues.locationId
						: record.locationId;

				if (isEditing) {
					const locationTreeData = [
						{
							title: 'Blank Location',
							value: '',
							key: 'blank',
						},
						...locationOptions,
					];
					return (
						<TreeSelect
							showSearch
							style={{ width: '100%' }}
							value={currentLocationId || ''}
							dropdownStyle={{
								maxHeight: 400,
								overflow: 'auto',
								minWidth: 250,
							}}
							treeData={locationTreeData}
							placeholder="Location"
							allowClear={false}
							treeDefaultExpandAll
							treeLine
							size="large"
							onChange={(value, label) => {
								const labelStr =
									Array.isArray(label) && label[0]
										? String(label[0])
										: label
											? String(label)
											: '';
								handleFieldChange(rowKey, 'locationId', value || '', labelStr);
							}}
							filterTreeNode={(inputValue, treeNode) => {
								return (
									treeNode.title
										?.toString()
										.toLowerCase()
										.includes(inputValue.toLowerCase()) || false
								);
							}}
						/>
					);
				}
				return (
					<div
						onClick={() => handleStartEdit(record)}
						style={{
							cursor: 'pointer',
							padding: '4px 0',
							minHeight: '22px',
							width: '100%',
						}}
					>
						{normalizeDisplayValue(locationName) || <span style={{ opacity: 0.5 }}></span>}
					</div>
				);
			},
		},
		{
			title: (
				<div style={{ position: 'relative', width: '100%' }}>
					Memo
					<div
						className={styles['resize-handle']}
						onMouseDown={(e) => handleMouseDown(e, 'memo')}
					/>
				</div>
			),
			dataIndex: 'description',
			key: 'description',
			ellipsis: true,
			width: columnWidths.memo,
		},
		{
			// title: 'Amount',
			title: (
				<div style={{ position: 'relative', width: '100%' }}>
					<div
						className={styles['sortable-header']}
						onClick={() => handleSort('amount')}
					>
						Amount
						{getSortIcon('amount')}
					</div>
					<div
						className={styles['resize-handle']}
						onMouseDown={(e) => handleMouseDown(e, 'amount')}
					/>
				</div>
			),
			dataIndex: 'amount',
			key: 'amount',
			align: 'right' as const,
			width: columnWidths.amount,
			render: (amount: number) => (
				<NumericFormat
					value={amount}
					thousandSeparator=","
					decimalScale={configurationsData?.decimalToFixedAmount || 2}
					fixedDecimalScale={true}
					displayType="text"
					prefix="$"
					renderText={(value) => (
						<span style={{ textAlign: 'right' }}>
							{value ?? 0}
						</span>
					)}
				/>
			),
		},
		{
			title: (
				<div style={{ position: 'relative', width: '100%' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
						Attachments
						<HistoryOutlined
							style={{
								fontSize: '12px',
								cursor: 'pointer',
								color: '#344735',
							}}
							onClick={handleHistoryClick}
							title="Download History"
						/>
					</div>
					<div
						className={styles['resize-handle']}
						onMouseDown={(e) => handleMouseDown(e, 'attachable')}
					/>
				</div>
			),
			dataIndex: 'attachable',
			key: 'attachable',
			ellipsis: true,
			width: columnWidths.attachable,
			align: 'center' as const,
			render: (_: boolean, record: ExpenseTransaction) => {
				const rowKey = `${record.txnId}:${record.qboLineId || 'no-line'}`;

				// Only show attachment icon if attachable flag is true
				if (!record.attachable) {
					return null;
				}

				return (
					<Tooltip title="Download all attachments for this transaction">
						{loadingRowKeys.has(rowKey) ? (
							<img src="/assets/gifs/loading-black.gif" height={20} />
						) : (
							<PaperClipOutlined
								style={{
									color: '#1890ff',
									cursor: 'pointer',
								}}
								onClick={() => handleClick(record)}
							/>
						)}
					</Tooltip>
				);
			},
		},
	];

	// Conditionally add Actions column only when a row is being edited
	if (editingRowKey) {
		columns.push({
			title: 'Actions',
			dataIndex: 'actions',
			key: 'actions',
			width: 120,
			align: 'center' as const,
			fixed: 'right' as const,
			render: (_: any, record: ExpenseTransaction) => {
				const rowKey = `${record.txnId}:${record.qboLineId || 'no-line'}:${record.entityType
					}`;
				const isEditing = editingRowKey === rowKey;
				const isSaving = savingRowKey === rowKey;

				if (!isEditing) {
					return null;
				}

				return (
					<Space size={10}>
						<div
							className="cursor-pointer flex align-center justify-center action-svg"
							onClick={() => !isSaving && handleSaveEdit(record)}
							style={{ opacity: isSaving ? 0.5 : 1 }}
						>
							{isSaving ? (
								<img src="/assets/gifs/loading-black.gif" height={40} />
							) : (
								<>
									<TickSmall />
								</>
							)}
						</div>
						<div
							className="cursor-pointer flex align-center justify-center action-svg"
							onClick={() => !isSaving && handleCancelEdit()}
							style={{ opacity: isSaving ? 0.5 : 1 }}
						>
							<CloseOutlined style={{ fontSize: '16px', color: '#ff4d4f' }} />
						</div>
					</Space>
				);
			},
		});
	}

	// Filter columns by visibleColumnKeys (select & actions always shown)
	const visibleColumns =
		visibleColumnKeys === undefined
			? columns
			: columns.filter(
					(col: any) =>
						col.key === 'select' ||
						col.key === 'actions' ||
						visibleColumnKeys.includes(col.key)
				);

	// Calculate total table width from visible columns only
	const totalTableWidth = visibleColumns.reduce(
		(sum, col) => sum + (col.width ?? 0),
		0
	);

	const hasActiveFilters = Boolean(
		selectedAccounts.length > 0 ||
		selectedCustomers.length > 0 ||
		selectedClasses.length > 0 ||
		selectedLocations.length > 0 ||
		selectedEntityTypes.length > 0 ||
		selectedLineBasedOn.length > 0
	);

	const CustomEmptyState = () => (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description={
				loading
					? null
					: hasActiveFilters
						? "No Expense Transaction records match the selected filter criteria"
						: "No Expense Transaction records are currently available"
			}
			style={{
				padding: '40px 20px',
				margin: '20px 0'
			}}
		/>
	);

	return (
		<div className={styles['expense-transaction-table']}>
			<Table
				columns={visibleColumns}
				dataSource={data}
				rowKey={(record) =>
					`${record.txnId}:${record.qboLineId || 'no-line'}:${record.entityType
					}`
				}
				pagination={false}
				scroll={{ x: Math.max(totalTableWidth, 1200), y: 'calc(87vh - 360px)' }}
				loading={loading}
				className="expense-transaction-table"
				locale={{
					emptyText: <CustomEmptyState />
				}}
			/>
			{/* Always visible grand total */}
			{data.length > 0 && (
				<div
					style={{
						padding: '0 20px',
						fontSize: '12px',
						color: '#344735',
						marginTop: '8px',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'flex-end',
						gap: '5px',
					}}
				>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<span style={{ fontWeight: 'bold' }}>Grand Total: </span>
						<NumericFormat
							value={grandTotal}
							thousandSeparator=","
							decimalScale={configurationsData?.decimalToFixedAmount || 2}
							fixedDecimalScale={true}
							displayType="text"
							prefix="$"
							renderText={(value) => (
								<span style={{ marginLeft: '4px', fontWeight: 'bold' }}>
									{value ?? 0}
								</span>
							)}
						/>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
						{isLoadingMore && (
							<>
								<Spin size="small" className={styles['loading-more-spinner']} />
								<span style={{ marginLeft: '8px' }}>
									Loading more transactions...
								</span>
							</>
						)}
						{showNoMoreMessage && (
							<span>No more transactions</span>
						)}
					</div>
				</div>
			)}
			<HistoryModal
				visible={isHistoryModalVisible}
				onCancel={handleHistoryModalClose}
			/>
		</div>
	);
};

export default ExpenseTransactionTable;
